import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Icon } from '@/nova/components';
import { Sheet, useSheetDismiss } from '@/nova/components/Sheet';

import { type GamificationFeature } from '../../gamification-sources';
import { useGamificationFeatures } from '../../useGamificationFeatures';
import * as S from './GamificationPickerSheet.styles';

export interface GamificationPickerSheetProps {
  /** The chosen features (multi-select) once the owner confirms. */
  onConfirm: (features: GamificationFeature[]) => void;
  /** Dismiss without choosing (backdrop / Escape / Cancel). */
  onClose: () => void;
  /** Feature ids to pre-select (editing an existing ad): checked on open once the active set resolves, so
   *  the owner can add/remove from the current features rather than starting over. */
  initialSelectedIds?: string[];
  /** Cap on the selection: unselected cards are disabled once this many are picked (mirrors the composer's
   *  promotion cap, so confirm never silently drops picks the draft can't hold). */
  max?: number;
}

/**
 * Pick the gamification feature(s) a video ad advertises, from the brand's ACTIVE features (configurator
 * catalogue ∩ purchases). Multi-select by feature id; confirm hands them up and the composer seeds a
 * storyboard from them. Empty when the brand has no active gamification features.
 */
export const GamificationPickerSheet = ({
  onConfirm,
  onClose,
  initialSelectedIds,
  max,
}: GamificationPickerSheetProps): React.ReactElement => {
  const { t } = useTranslation();
  const { closeHandle } = useSheetDismiss(onClose);
  const { features, loading } = useGamificationFeatures();
  const [selected, setSelected] = React.useState<Record<string, GamificationFeature>>({});
  const selectedList = Object.values(selected);
  const atMax = max != null && selectedList.length >= max;

  // Pre-select the ad's already-chosen features ONCE the active set resolves (it may arrive after mount).
  // Merge under any picks the owner already made (`...prev` wins), and seed only once so a later deselect
  // is not undone.
  const seededRef = React.useRef(false);
  React.useEffect(() => {
    if (seededRef.current) return;
    if (!initialSelectedIds || initialSelectedIds.length === 0) {
      seededRef.current = true;
      return;
    }
    if (features.length === 0) return; // wait for the active set before resolving ids
    const byId = new Map(features.map((f) => [f.id, f]));
    const seeded = Object.fromEntries(
      initialSelectedIds
        .map((id) => byId.get(id))
        .filter((f): f is GamificationFeature => Boolean(f))
        .map((f) => [f.id, f]),
    );
    setSelected((prev) => ({ ...seeded, ...prev }));
    seededRef.current = true;
  }, [initialSelectedIds, features]);

  const toggle = (feature: GamificationFeature): void =>
    setSelected((prev) => {
      const next = { ...prev };
      if (next[feature.id]) delete next[feature.id];
      // Self-gate the cap (styling alone does not block a click): never add past `max`.
      else if (!(max != null && Object.keys(prev).length >= max)) next[feature.id] = feature;
      return next;
    });

  return (
    <Sheet
      title="Choose gamification features to advertise"
      onClose={onClose}
      closeHandle={closeHandle}
      maxWidth="820px"
      footer={
        <S.FooterRow>
          <S.SelCount text="{{n}} selected" placeholders={{ n: selectedList.length }} />
          <S.FooterActions>
            <Button variant="text" label={t('Cancel')} onClick={onClose} />
            <Button
              variant="primary"
              label={t('Use selected features')}
              onClick={() => onConfirm(selectedList)}
              disabled={selectedList.length === 0}
            />
          </S.FooterActions>
        </S.FooterRow>
      }
    >
      {loading ? <S.Note text="Loading features…" /> : null}
      {!loading && features.length === 0 ? (
        <S.Note text="This brand has no active gamification features yet." />
      ) : null}

      <S.Grid>
        {features.map((feature) => {
          const isSelected = Boolean(selected[feature.id]);
          const disabled = !isSelected && atMax;
          return (
            <S.FeatureCard
              key={feature.id}
              type="button"
              $disabled={disabled}
              aria-pressed={isSelected}
              aria-disabled={disabled}
              onClick={() => {
                if (disabled) return; // handler self-gates the max (styling alone does not block a click)
                toggle(feature);
              }}
            >
              <S.Thumb $selected={isSelected}>
                {feature.image ? <S.ThumbImg src={feature.image} alt="" loading="lazy" draggable={false} /> : null}
                {isSelected ? (
                  <S.Check aria-hidden="true">
                    <Icon name="check" size={13} />
                  </S.Check>
                ) : null}
              </S.Thumb>
              <S.Name text={feature.name} />
            </S.FeatureCard>
          );
        })}
      </S.Grid>
    </Sheet>
  );
};

export default GamificationPickerSheet;
